/**
 * @file oxpay.js
 * @description OxPay API 클라이언트 - 핵심 HTTP 통신 레이어
 *
 * 이 모듈은 모든 OxPay API 작업을 위한 깔끔한 인터페이스를 제공합니다.
 * 보안을 위해 모든 요청은 Cloudflare Functions를 통해 프록시됩니다.
 *
 * 아키텍처:
 * 프론트엔드 (이 파일) → Cloudflare Functions → OxPay API
 *
 * 주요 기능:
 * - 일관된 에러 처리
 * - RESTful API 디자인
 * - 타입 안전한 메서드 시그니처
 *
 * 보안 참고사항:
 * - 민감한 데이터(키, 자격증명) 저장하지 않음
 * - 모든 서명은 Cloudflare Functions에서 서버사이드로 처리
 *
 * @module lib/oxpay
 */

const API_BASE = import.meta.env.VITE_PROXY_API_SERVER;

class OxPayAPI {
  async request(functionName, method, endpoint, data = {}) {
    const requestPayload = {
      request: {
        head: {
          function: functionName,
          method: method,
        },
        body: data,
      },
    };

    const options = {
      method: "POST", // All OxPay requests use POST
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestPayload),
    };

    try {
      // Build URL with endpoint path
      let url = `${API_BASE}/api/v1/oxpay${endpoint}`;
      url = url.replace(/([^:]\/)\/+/g, "$1");

      const response = await fetch(url, options);
      const result = await response.json();

      if (result?.headers?.status_code === "error") {
        throw new Error(result?.body?.description || result?.body?.title);
      }

      if (!response.ok) {
        throw new Error(result.message || `Error: ${response.status}`);
      }

      return result;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  }

  // User Management
  async createUser(userData) {
    return this.request("oxpay.users", "POST", "/issuing/users", userData);
  }

  async getUser(userId) {
    return this.request("oxpay.users", "GET", "/issuing/users", { id: userId });
  }

  async updateUser(userId, updates) {
    return this.request("oxpay.users", "PUT", "/issuing/users", {
      id: userId,
      ...updates,
    });
  }

  async deleteUser(userId) {
    return this.request("oxpay.users", "DELETE", "/issuing/users", {
      id: userId,
    });
  }

  // KYC & Document Verification
  async startKYC(userId) {
    return this.request(
      "oxpay.users.authentications",
      "POST",
      "/issuing/users",
      { id: userId },
    );
  }

  async getKYCStatus(userId) {
    return this.request(
      "oxpay.users.authentications",
      "GET",
      "/issuing/users",
      { id: userId },
    );
  }

  // POA Document Upload (via Onfido)
  async uploadPOADocument(userId, documentData) {
    // POA is handled through the same KYC/Onfido flow
    // This method prepares the user for document verification
    return this.request(
      "oxpay.users.authentications",
      "POST",
      "/issuing/users",
      {
        id: userId,
        document_type: "proof_of_address",
        ...documentData,
      },
    );
  }

  // Address Management
  async getUserAddress(userId, data = { type_address: "residential" }) {
    return this.request("oxpay.users.addresses", "GET", "/issuing/users", {
      id: userId,
      ...data,
    });
  }

  async updateUserAddress(userId, addressData) {
    return this.request("oxpay.users.addresses", "PUT", "/issuing/users", {
      id: userId,
      type_address: addressData.type_address || "residential",
      address_1: addressData?.address_1?.trim() || "",
      address_2: addressData?.address_2?.trim() || "",
      address_3: addressData?.address_3?.trim() || "",
      address_4: addressData?.address_4?.trim() || "",
      city: addressData?.city?.trim() || "",
      state: addressData?.state?.trim() || "",
      country: addressData?.country?.trim() || "",
      country_code: addressData?.country_code?.trim() || "",
      zipcode: addressData.zipcode?.trim() || "",
    });
  }

  // Wallet
  async createWallet(userId, walletData = {}) {
    return this.request("oxpay.users.wallets", "POST", "/issuing/users", {
      id: userId,
      ...walletData,
    });
  }

  // Cards
  async getCards(userId) {
    return this.request("oxpay.users.cards", "GET", "/issuing/cards", {
      id: userId,
    });
  }

  async createCard(userId, cardData = {}) {
    return this.request("oxpay.users.cards", "POST", "/issuing/cards", {
      id: userId,
      type: cardData.type || "VIRTUAL",
      currency: cardData.currency || "USD",
      ...cardData,
    });
  }

  async getCard(cardId) {
    return this.request("oxpay.users.cards", "GET", "/issuing/cards", {
      card_id: cardId,
    });
  }

  async updateCard(cardId, action, data = {}) {
    return this.request("oxpay.users.cards", "POST", "/issuing/cards", {
      card_id: cardId,
      action,
      ...data,
    });
  }

  // Card Actions
  async activateCard(cardId, lastFour) {
    return this.request("oxpay.users.cards", "PUT", "/issuing/cards", {
      card_id: cardId,
      lastFour,
    });
  }

  async lockCard(cardId) {
    return this.request("oxpay.users.cards", "DELETE", "/issuing/cards", {
      card_id: cardId,
    });
  }

  async unlockCard(cardId) {
    return this.request("oxpay.users.cards", "POST", "/issuing/cards", {
      card_id: cardId,
      action: "unlock",
    });
  }

  async setPin(cardId, userId, pin, mobileNumber, mobileCountryCode) {
    return this.request("oxpay.users.cards", "POST", "/issuing/cards", {
      card_id: cardId,
      id: userId,
      action: "pin",
      pin,
      mobile_number: mobileNumber,
      mobile_country_code: mobileCountryCode,
    });
  }

  async getCardTransactions(userId, cardId, options = {}) {
    const body = {
      id: userId,
      card_id: cardId,
      page: options.page || 1,
      limit: options.limit || 10,
    };

    if (options.date_range) {
      body.date_range = options.date_range;
    }

    return this.request("oxpay.users.cards", "GET", "/issuing/cards", body);
  }

  // Wallet
  async getWallet(userId) {
    return this.request("oxpay.users.wallets", "GET", "/issuing/wallet", {
      id: userId,
    });
  }

  async topUp(userId, amount, method = "BANK_TRANSFER") {
    return this.request("oxpay.users.wallets", "POST", "/issuing/wallet", {
      id: userId,
      f2w: "true",
      amount,
      method,
    });
  }

  async transfer(fromUserId, toUserId, amount, description = "") {
    return this.request("oxpay.users.wallets", "POST", "/issuing/wallet", {
      sender_id: fromUserId,
      p2p: "true",
      recipientId: toUserId,
      amount,
      description,
    });
  }

  // Transactions
  async getTransactions(userId, options = {}) {
    const body = {
      id: userId,
      page: options.page || 1,
      limit: options.limit || 50,
      offset: options.offset || 0,
    };

    if (options.date_range) {
      body.date_range = options.date_range;
    }

    return this.request("oxpay.users.wallets", "GET", "/issuing/wallet", body);
  }

  // async getTransaction(transactionId) {
  //   return this.request("oxpay.transactions", "GET", "/issuing/transactions", {
  //     transactionId,
  //   });
  // }

  // Enumerations
  async getEnumerations(type) {
    return this.request(
      "oxpay.users.enumerations",
      "GET",
      "/issuing/enumerations",
      { enumeration_type: type },
    );
  }

  async getCountries() {
    return this.getEnumerations("countries");
  }

  async getMobileCountryCodes() {
    return this.getEnumerations("mobile_country_codes");
  }

  // Prefund Management
  async getPrefundBalance() {
    return this.request(
      "oxpay.merchants.prefunds",
      "GET",
      "/issuing/prefunds",
      {}, // company_id is set by the proxy from env
    );
  }

  async getPrefundHistory() {
    return this.request(
      "oxpay.merchants.prefunds",
      "GET",
      "/issuing/prefunds",
      { prefund_history: "true" },
    );
  }
}

export const oxpayAPI = new OxPayAPI();
